package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.FileFormat
import dev.lingvo.shared.ImportPlanDto
import dev.lingvo.shared.ImportRequest
import dev.lingvo.shared.KeyChange
import dev.lingvo.shared.KeyDiffDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.components.ExportCard
import dev.lingvo.site.components.ProjectFilesObserver
import dev.lingvo.site.components.rememberExportSetup
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LDownloadIcon
import dev.lingvo.site.ds.LCheckCheckIcon
import dev.lingvo.site.ds.LFileIcon
import dev.lingvo.site.ds.LGitBranchIcon
import dev.lingvo.site.ds.LHistoryIcon
import dev.lingvo.site.ds.LListChecksIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LStatTile
import dev.lingvo.site.ds.LStep
import dev.lingvo.site.ds.LSteps
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LUploadIcon
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import dev.lingvo.site.ds.LWandSparklesIcon
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.enterStaggered
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.nav.UrlState
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.util.downloadBlob
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TagElement
import org.w3c.dom.Element
import kotlin.js.Date
import org.w3c.files.File
import org.w3c.files.FileReader
import org.w3c.files.get

/** Past this, the names stop being evidence and become a wall. The counts above still cover all. */
private const val MAX_OBSOLETE_NAMES = 50

/**
 * Getting strings in and out, with a plan step nobody can skip.
 *
 * Replaces `/import-export`. The difference is not cosmetic: an import used to apply the moment
 * somebody clicked, and the only way to find out what it did was to read the result. Here every
 * import is planned first, the plan is shown **as a per-key diff rather than counts** — counts are
 * how you hide a mangled plural, and `willUpdate: 412` is not evidence that a `.stringsdict`
 * survived — and committing names the plan by id, so there is no way to agree to one thing and
 * commit another.
 *
 * Above 20% obsoletion the server refuses without an explicit count, for **every** caller. This page
 * therefore replaces the Import button with a typed confirmation naming the number, which is the
 * only way to supply something an autonomous loop cannot invent.
 *
 * **All three steps are always mounted.** Steps 2 and 3 used to appear only once their precondition
 * was met, so somebody looking at the file picker had no way to know what came next — the page grew
 * a card at a time, and each arrival shoved the export card down. They are drawn from the start,
 * with the precondition written where the control would be.
 */
@Page("/app/projects/{slug}/data")
@Layout(".components.layouts.AppLayout")
@Composable
fun ProjectDataPage() {
    val ctx = rememberPageContext()
    val slug = ctx.route.params["slug"].orEmpty()
    val requestedFileId = ctx.route.queryParams["fileId"]
    // **The export half of the deep link.** `ReadinessBand` links a language's row here as
    // "Export German" (`?locale=de`), and the files table's menu offers other formats — both land in
    // `ExportCard`, so the parameters are read here and handed down rather than re-read there: the
    // card is also mounted on surfaces that have no page context of their own. `format` accepts both
    // the enum name the export select emits and the wire `id`, because a link written by hand from
    // the OpenAPI file uses the latter.
    val requestedLocale = UrlState.read(ctx, "locale")
    val requestedFormat = UrlState.read(ctx, "format")?.let { raw ->
        FileFormat.entries.firstOrNull { it.name.equals(raw, ignoreCase = true) } ?: FileFormat.fromId(raw)
    }
    val scope = rememberCoroutineScope()

    val route = liveProjectRoute(slug)
    val subject = route?.authoritySubject(Session.organizations)
    val flow = remember(subject) { ImportPreviewState(subject) }
    val filesObserver = remember(flow) {
        ProjectFilesObserver(flow::destinationFilesStarted, flow::destinationFilesLoaded, flow::destinationFilesFailed)
    }
    val fileSetup = rememberExportSetup(slug, subject, flow.exportRefreshToken, filesObserver, canRetry = { !flow.busy })
    var archiving by remember { mutableStateOf(false) }
    var archiveMessage by remember { mutableStateOf<String?>(null) }
    val authority = ProjectAuthority.resolve(route?.project, Session.organizations)
    val canImport = authority.canEditStructure
    val chosen = flow.file
    val locale = flow.locale
    val format = flow.format
    val plan = flow.plan
    val confirmation = flow.confirmation
    val busy = flow.busy
    val readyToPlan = flow.readyToPlan
    fun claim() = ProjectActionClaim.capture(liveProjectRoute(slug), Session.organizations, ProjectPrivilege.STRUCTURE)
    val currentRoute = { liveProjectRoute(slug) }
    val currentMemberships = { Session.organizations }
    val currentStep = when {
        flow.done != null -> 3
        plan != null -> 2
        readyToPlan -> 1
        else -> 0
    }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            t("app.projects.slug.data.data", "Data"),
            t("app.projects.slug.data.bring_strings_in_take_them_out", "Bring strings in, take them out. Preview key and translation changes before applying them."),
            actions = {
                if (authority.canSpendCredits) {
                    LButton(
                        t("app.projects.slug.data.pre_translate", "Pre-translate"),
                        { ctx.router.tryRoutingTo("/app/projects/$slug/pretranslate") },
                        variant = LButtonVariant.SECONDARY,
                        leadingIcon = { LWandSparklesIcon() },
                    )
                }
                if (authority.canReadProject) {
                    LButton(
                        t("app.projects.slug.data.translation_runs", "Translation runs"),
                        { ctx.router.tryRoutingTo("/app/projects/$slug/data/jobs") },
                        variant = LButtonVariant.SECONDARY,
                        leadingIcon = { LHistoryIcon() },
                    )
                }
            },
        )

        flow.done?.let {
            LAlert(t("app.projects.slug.data.imported", "Imported"), it, variant = LAlertVariant.SUCCESS, live = true)
        }
        (flow.message ?: archiveMessage)?.let {
            LAlert(
                t("app.projects.slug.data.that_didnt_work", "That didn't work"),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }

        if (flow.inspectRequired) {
            LLinkButton(
                t("app.projects.slug.data.inspect_current_project", "Inspect current project"),
                path = "/app/projects/$slug/keys",
                variant = LButtonVariant.SECONDARY,
            )
        }

        if (!canImport) {
            LAlert(
                title = t(
                    "app.projects.slug.data.importing_is_managed_by_an_owner_or_admin",
                    "Importing is managed by an owner or admin",
                ),
                description = t("app.projects.slug.data.importing_adds_updates_and_obsoletes_keys_2", "Importing adds, updates and obsoletes keys for everyone in this " +
                    "project. You can still export any file below in any format."),
                variant = LAlertVariant.INFO,
            )
        }

        if (canImport) {
            DataImportSteps(currentStep)

            // The other way strings get in, offered beside the file picker rather than only during
            // onboarding. Somebody who made this project by hand — or with `lingvo init` — had no way
            // to pull from a repository at all without creating a second project for the same app.
            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    LSectionTitle(
                        t("app.projects.slug.data.choose_a_file", "Choose a file"),
                        icon = { LFileIcon() },
                    )
                    // **The picker is a control of ours, not the browser's.** A bare
                    // `<input type=file>` paints itself: user-agent font, user-agent grey button,
                    // square corners, and the words "No file chosen" in English whatever the
                    // reader's locale — the one object on this page that ignores the colour mode,
                    // and in Night the brightest thing on the card. The input is kept (it is what
                    // opens the file dialog and what assistive technology operates) and hidden;
                    // the label around it is the visible control, styled like the secondary
                    // buttons it sits beside. The chosen file's name is stated below, beside the
                    // language and format it was detected as.
                    Label(attrs = DataFilePickerLabelStyle.toModifier().toAttrs()) {
                        Input(
                            type = InputType.File,
                            attrs = LVisuallyHiddenStyle.toModifier().toAttrs {
                                attr("aria-label", tr("app.projects.slug.data.localization_file", "Localization file"))
                                onChange { event ->
                                    val picked = event.target.files?.get(0) ?: return@onChange
                                    flow.chooseFile(picked, detectLocale(picked.name), detectFormat(picked.name))
                                    readText(
                                        picked,
                                        onLoaded = flow::fileLoaded,
                                        onFailed = flow::fileFailed,
                                    )
                                }
                            },
                        )
                        LUploadIcon(Modifier.iconSize(IconSize.SM))
                        Text(t("app.projects.slug.data.choose_a_file", "Choose a file"))
                    }
                    chosen?.let { picked ->
                        Row(
                            Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap),
                            verticalAlignment = Alignment.Bottom,
                        ) {
                            LTextField(
                                locale,
                                flow::changeLocale,
                                Modifier.flexGrow(1),
                                label = t("app.projects.slug.data.language", "Language"),
                                placeholder = "de",
                            )
                            LMuted(picked.name)
                            // **Detected, and now actually overridable.** The format was read off the
                            // extension and there was no way to correct it — and the whole import
                            // flow is gated on a non-null format, so a file whose extension was not
                            // recognised produced the words "unknown format" and nothing else.
                            LSelect(
                                value = format?.name.orEmpty(),
                                options = listOf(
                                    LSelectOption("", t("app.projects.slug.data.choose_the_format", "Choose the format…")),
                                ) + FileFormat.entries.map { LSelectOption(it.name, it.displayName) },
                                onValueChange = { raw ->
                                    flow.changeFormat(FileFormat.entries.firstOrNull { it.name == raw })
                                },
                                label = t("app.projects.slug.data.incoming_file_format", "Import format"),
                            )
                        }
                        ImportDestinationControls(flow, onRefresh = fileSetup.retry)
                        if (format == null) {
                            LAlert(
                                t("app.projects.slug.data.we_couldnt_tell_what_this_file", "We couldn't tell what this file is"),
                                t("app.projects.slug.data.pick_the_format_above_and_the_2", "Pick the format above and the import will carry on. If none of " +
                                    "them fits, the formats page lists everything we read."),
                                variant = LAlertVariant.INFO,
                            )
                        }
                    }
                    LButton(
                        t("app.projects.slug.data.choose_a_repository", "Choose a repository"),
                        { ctx.router.tryRoutingTo("/app/start/github?project=$slug") },
                        variant = LButtonVariant.SECONDARY,
                        leadingIcon = { LGitBranchIcon() },
                    )
                    LMuted(
                        t("app.projects.slug.data.pull_the_localization_files_straight_out_2", "Pull the localization files straight out of a repository. Lingvo reads them, " +
                            "detects the format and languages, and imports into this project. Read-only " +
                            "— it never writes to your code."),
                    )
                }
            }

            DataPlanCard(
                plan = plan,
                readyToPlan = readyToPlan,
                busy = busy,
                planned = flow.planned,
                onPlan = {
                    val operation = claim()?.let(flow::beginPreview)
                    if (operation != null) scope.launch { flow.prepare(operation, currentRoute, currentMemberships) }
                },
            )

            DataApplyCard(
                plan = plan,
                confirmation = confirmation,
                onConfirmation = { flow.confirmation = it },
                busy = busy,
                planned = flow.planned,
                onImport = {
                    val operation = claim()?.let(flow::beginCommit)
                    if (operation != null) scope.launch { flow.apply(operation, currentRoute, currentMemberships) }
                },
            )
        }

        ExportCard(slug, requestedFileId, requestedLocale, requestedFormat, fileSetup)

        // **The exit, offered.** `GET /export/all` has existed since the archive was built and no
        // screen in the product pointed at it, so the answer to "can I leave" was a route only
        // somebody reading the OpenAPI file could find.
        LCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                LSectionTitle(
                    t("app.projects.slug.data.take_everything_with_you", "Take everything with you"),
                    icon = { LDownloadIcon() },
                )
                LMuted(
                    t("app.projects.slug.data.one_archive_with_the_strings_the_2", "One archive with the strings in their own formats, the translation memory as " +
                        "TMX, the glossary as CSV, and the comments and history as JSON. Nothing is " +
                        "held back."),
                )
                LButton(
                    t("app.projects.slug.data.download_everything", "Download everything"),
                    {
                        scope.launch {
                            archiving = true
                            archiveMessage = null
                            try {
                                downloadBlob(ApiClient.exportEverything(slug), "$slug-export.zip")
                            } catch (cancelled: CancellationException) {
                                throw cancelled
                            } catch (failure: Throwable) {
                                archiveMessage = (failure as? ApiException)?.message?.takeIf(String::isNotBlank)
                                    ?: tr(
                                        "app.projects.slug.data.couldnt_build_the_archive_retry",
                                        "Couldn't build the archive. Retry?",
                                    )
                            } finally {
                                archiving = false
                            }
                        }
                    },
                    variant = LButtonVariant.SECONDARY,
                    enabled = !archiving,
                    loading = archiving,
                    leadingIcon = { LDownloadIcon() },
                )
            }
        }
    }
}

/**
 * ② The plan step: what the chosen file would do, before keys/translations are applied.
 *
 * The heading carries no ordinal of its own. [DataImportSteps] above numbers the three steps and is
 * the only thing that can also say which one the reader is standing on; a "2." welded into this
 * title numbered the same flow a second time, and the rail — the half that can show state — read
 * as decoration beside it.
 *
 * **The accent moves to card ③ once a plan exists.** Re-planning prepares a preview;
 * importing writes and can obsolete keys. Two accent buttons on one screen state two answers to
 * "what do I press now", and preparing a preview looked exactly as loud as applying its changes.
 */
@Composable
internal fun DataPlanCard(
    plan: ImportPlanDto?,
    readyToPlan: Boolean,
    busy: Boolean,
    onPlan: () -> Unit,
    planned: ImportRequest? = null,
) {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            LSectionTitle(
                t("app.projects.slug.data.see_what_it_would_do", "See what it would do"),
                icon = { LListChecksIcon() },
            )
            if (!readyToPlan) {
                // The precondition, written where the control would be. A dimmed card with
                // nothing in it reads as a bug.
                LMuted(
                    t(
                        "app.projects.slug.data.choose_file_destination_language",
                        "Choose a file, confirm its destination and language, and the plan appears here.",
                    ),
                )
            } else {
                LButton(
                    if (plan == null) {
                        t("app.projects.slug.data.plan_and_import", "Plan and import")
                    } else {
                        t("app.projects.slug.data.re_plan", "Re-plan")
                    },
                    onPlan,
                    // **The re-run is not the screen's task.** Once a plan exists, "Import"
                    // in card 3 is the one thing the reader is being asked to decide, and an
                    // accent "Re-plan" beside it gave the harmless re-run exactly the weight
                    // of the write. Before there is a plan this button *is* the task, and
                    // keeps the accent.
                    variant = if (plan == null) LButtonVariant.PRIMARY else LButtonVariant.SECONDARY,
                    enabled = !busy,
                    loading = busy,
                    leadingIcon = { LUploadIcon() },
                )
                LMuted(
                    t(
                        "app.projects.slug.data.nothing_is_written_until_you_read",
                        "Key and translation changes are not applied until you read the preview and press Import. Preparing the preview may register the file and its export settings.",
                    ),
                )
            }
            plan?.let { current ->
                planned?.let { DataPreviewIdentity(it, current) }
                PlanCard(current, Modifier.enterStaggered(0))
            }
        }
    }
}

/**
 * ③ The commit step, and the screen's one primary action once there is a plan to agree to.
 *
 * Danger rather than accent when the plan would obsolete keys: that press is the destructive one,
 * and it is gated on the reader typing the count back.
 */
@Composable
internal fun DataApplyCard(
    plan: ImportPlanDto?,
    confirmation: String,
    onConfirmation: (String) -> Unit,
    busy: Boolean,
    onImport: () -> Unit,
    planned: ImportRequest? = null,
) {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            LSectionTitle(
                t("app.projects.slug.data.apply_it", "Apply it"),
                icon = { LCheckCheckIcon() },
            )
            val current = plan
            if (current == null) {
                LMuted(
                    t(
                        "app.projects.slug.data.plan_the_import_first_and_this",
                        "Plan the import first, and this is where you agree to it.",
                    ),
                )
            } else {
                planned?.let { DataPreviewIdentity(it, current) }
                if (current.requiresForce) {
                    LAlert(
                        // The whole sentence goes through `tPlural`, not "This would remove "
                        // + a count: a translator handed a clause ending mid-air cannot put
                        // the number where their own grammar wants it.
                        tPlural(
                            "app.projects.slug.data.count.would_remove",
                            current.willObsolete,
                            "This would remove {count} key",
                            "This would remove {count} keys",
                        ),
                        // The sentence somebody reads immediately before obsoleting a
                        // feature's worth of strings.
                        tf(
                            "app.projects.slug.data.that_is_percent_of_this_file",
                            "That is {percent}% of this file. If the file you picked is a " +
                                "partial export, this is how a whole feature's strings " +
                                "disappear. Type {count} to confirm you mean it.",
                            "percent" to (current.obsoleteRatio * 100).toInt(),
                            "count" to current.willObsolete,
                        ),
                        variant = LAlertVariant.DANGER,
                    )
                    LTextField(
                        confirmation,
                        onConfirmation,
                        Modifier.fillMaxWidth(),
                        label = t("app.projects.slug.data.number_of_keys_to_remove", "Number of keys to remove"),
                        placeholder = current.willObsolete.toString(),
                    )
                }
                LButton(
                    t("app.projects.slug.data.import", "Import"),
                    onImport,
                    enabled = !busy &&
                        (!current.requiresForce || confirmation.trim().toIntOrNull() == current.willObsolete),
                    loading = busy,
                    variant = if (current.requiresForce) LButtonVariant.DANGER else LButtonVariant.PRIMARY,
                )
            }
        }
    }
}

@Composable
private fun DataPreviewIdentity(request: ImportRequest, plan: ImportPlanDto) {
    Column(Modifier.fillMaxWidth().gap(Space.XXS).attr("data-import-preview-identity", "true")) {
        LMuted(tf("app.projects.slug.data.captured_destination", "File: {path} · Language: {locale} · Format: {format}",
            "path" to request.path, "locale" to request.locale, "format" to request.format.displayName))
        val expires = Date(plan.expiresAt)
        TagElement<Element>("time", applyAttrs = { attr("datetime", plan.expiresAt) }) {
            Text(tf("app.projects.slug.data.preview_expiry", "Preview expires: {time}",
                "time" to if (expires.getTime().isNaN()) plan.expiresAt else expires.toLocaleString()))
        }
    }
}

/**
 * Where the import has got to, drawn before the cards it describes.
 *
 * The three cards below are always mounted, so this is the only thing that says which one the
 * reader is on. Everything earlier than [currentStep] is marked done from the *set*, never from
 * position — a step is complete because its precondition was met, and a flow that inferred it from
 * the index would mark a step done the moment the reader skipped past it.
 */
@Composable
internal fun DataImportSteps(currentStep: Int) {
    LSteps(
        steps = listOf(
            LStep(
                t("app.projects.slug.data.choose_a_file", "Choose a file"),
                t("app.projects.slug.data.from_your_machine_or_a_repository", "From your machine, or a repository"),
            ),
            LStep(
                t("app.projects.slug.data.see_what_it_would_do", "See what it would do"),
                t("app.projects.slug.data.a_per_key_diff_before_anything", "A per-key preview before key and translation changes"),
            ),
            LStep(
                t("app.projects.slug.data.apply_it", "Apply it"),
                t("app.projects.slug.data.commits_the_plan_you_just_read", "Commits the plan you just read"),
            ),
        ),
        current = currentStep,
        modifier = Modifier.fillMaxWidth(),
        completed = (0 until currentStep).toSet(),
    )
}

/**
 * The plan, as a diff.
 *
 * Counts first because they are the shape of the change, then the per-key before and after, because
 * counts are how a mangled plural hides. `willUpdate: 412` is not evidence that anything survived.
 */
@Composable
private fun PlanCard(plan: ImportPlanDto, modifier: Modifier = Modifier) {
    Column(Modifier.fillMaxWidth().gap(Space.MD).then(modifier)) {
        Row(Modifier.fillMaxWidth().gap(Space.MD).flexWrap(FlexWrap.Wrap)) {
            LStatTile(t("app.projects.slug.data.new", "New"), formatCount(plan.willCreate), compact = true)
            LStatTile(t("app.projects.slug.data.changed", "Changed"), formatCount(plan.willUpdate), compact = true)
            LStatTile(
                t("app.projects.slug.data.removed", "Removed"),
                formatCount(plan.willObsolete),
                compact = true,
                emphasis = plan.willObsolete > 0,
                tone = LBadgeVariant.DANGER,
            )
            LStatTile(t("app.projects.slug.data.skipped", "Skipped"), formatCount(plan.willSkip), compact = true)
            LStatTile(t("app.projects.slug.data.unchanged", "Unchanged"), formatCount(plan.willUnchange), compact = true)
        }

        if (plan.willSkip > 0) {
            // Its own line with the reason. A wall of "unchanged" reading as success is a known
            // failure of the surface this replaces.
            LMuted(
                tPlural(
                    "app.projects.slug.data.count.skipped_key",
                    plan.willSkip,
                    "{count} key was skipped because it does not exist in the source locale yet. " +
                        "Import the source file first and they will land.",
                    "{count} keys were skipped because they do not exist in the source locale yet. " +
                        "Import the source file first and they will land.",
                ),
            )
        }

        plan.warnings.forEach { warning ->
            LAlert(t("app.projects.slug.data.worth_checking", "Worth checking"), warning, variant = LAlertVariant.WARNING)
        }

        if (plan.obsoleteKeys.isNotEmpty()) {
            ObsoleteKeys(plan.obsoleteKeys)
        }

        if (plan.diff.isNotEmpty()) {
            Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                Span(Modifier.fontWeight(600).fontSize(TypeScale.BodySmall).toAttrs()) {
                    Text(t("app.projects.slug.data.line_by_line", "Line by line"))
                }
                plan.diff.forEach { row -> DiffRow(row) }
                if (plan.diffTruncated) {
                    LMuted(
                        tPlural(
                            "app.projects.slug.data.diff_truncated",
                            plan.diff.size,
                            "Only the first row is shown. The counts above cover the whole file.",
                            "Only the first {count} rows are shown. The counts above cover the whole file.",
                        ),
                    )
                }
            }
        }
    }
}

/**
 * The names about to disappear, grouped by what they belong to.
 *
 * Named, not counted: the names are how somebody recognises that a whole feature area is going. But
 * four hundred of them in one column is a wall nobody reads, and the thing they are trying to see —
 * "everything under `checkout.` is about to go" — is exactly what the leading segment says. So they
 * are grouped by it, capped, and put in a scroll well of their own rather than pushing the Import
 * button below the fold.
 */
@Composable
private fun ObsoleteKeys(keys: List<String>) {
    val grouped = keys.groupBy { it.substringBefore('.', it) }
    Column(Modifier.fillMaxWidth().gap(Space.XS)) {
        Span(Modifier.fontWeight(600).fontSize(TypeScale.BodySmall).toAttrs()) {
            Text(t("app.projects.slug.data.would_be_removed", "Would be removed"))
        }
        Column(
            Modifier
                .fillMaxWidth()
                .gap(Space.SM)
                .maxHeight(18.cssRem)
                .overflow { y(Overflow.Auto) },
        ) {
            var shown = 0
            grouped.forEach { (group, names) ->
                if (shown >= MAX_OBSOLETE_NAMES) return@forEach
                val room = MAX_OBSOLETE_NAMES - shown
                Column(Modifier.fillMaxWidth().gap(Space.XXS)) {
                    Row(Modifier.gap(Space.XS), verticalAlignment = Alignment.CenterVertically) {
                        Span(Modifier.monoFont().fontSize(TypeScale.Small).fontWeight(600).toAttrs()) {
                            Text(group)
                        }
                        LBadge(formatCount(names.size), variant = LBadgeVariant.DANGER)
                    }
                    names.take(room).forEach { key ->
                        Span(Modifier.monoFont().fontSize(TypeScale.Small).toAttrs()) { Text(key) }
                    }
                }
                shown += minOf(room, names.size)
            }
        }
        if (keys.size > MAX_OBSOLETE_NAMES) {
            LMuted(
                tf(
                    "app.projects.slug.data.showing_shown_of_total_names",
                    "Showing {shown} of {total} names. The counts above cover all of them.",
                    "shown" to MAX_OBSOLETE_NAMES,
                    "total" to keys.size,
                ),
            )
        }
    }
}

@Composable
private fun DiffRow(row: KeyDiffDto) {
    Column(Modifier.fillMaxWidth().gap(Space.XXS)) {
        Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
            Span(Modifier.monoFont().fontSize(TypeScale.Small).toAttrs()) { Text(row.key) }
            LBadge(
                row.change.changeWords(),
                variant = when (row.change) {
                    KeyChange.OBSOLETE -> LBadgeVariant.DANGER
                    KeyChange.CREATE -> LBadgeVariant.SUCCESS
                    else -> LBadgeVariant.NEUTRAL
                },
            )
        }
        // Plurals get their categories rather than one flattened string, because "one form changed"
        // is precisely the thing a single line would hide.
        row.beforePlural?.forEach { (category, value) -> LMuted(tf("app.projects.slug.data.plural_before", "{category} was: {value}", "category" to category, "value" to value)) }
            ?: row.before?.let { LMuted(tf("app.projects.slug.data.before", "was: {value}", "value" to it)) }
        row.afterPlural?.forEach { (category, value) -> LMuted(tf("app.projects.slug.data.plural_after", "{category} becomes: {value}", "category" to category, "value" to value)) }
            ?: row.after?.let { LMuted(tf("app.projects.slug.data.after", "becomes: {value}", "value" to it)) }
    }
}

/** Written words, never `name.lowercase()`: an enum name on a badge is untranslatable. */
private fun KeyChange.changeWords(): String = when (this) {
    KeyChange.CREATE -> tr("app.projects.slug.data.change_create", "new")
    KeyChange.UPDATE -> tr("app.projects.slug.data.change_update", "changed")
    KeyChange.OBSOLETE -> tr("app.projects.slug.data.change_obsolete", "removed")
    KeyChange.SKIP -> tr("app.projects.slug.data.change_skip", "skipped")
    KeyChange.UNCHANGED -> tr("app.projects.slug.data.change_unchanged", "unchanged")
}

/**
 * The language a file is for, from its path.
 *
 * `values-de/strings.xml`, `de.lproj/Localizable.strings`, `locales/de.json` all say `de` out loud.
 * Making somebody retype it is how a French file gets imported as German.
 */
private fun detectLocale(path: String): String {
    val segments = path.split('/', '\\')
    segments.forEach { segment ->
        Regex("^values-([a-z]{2}(?:-r[A-Z]{2})?)$").find(segment)?.let {
            return it.groupValues[1].replace("-r", "-")
        }
        Regex("^([a-z]{2}(?:-[A-Z]{2})?)\\.lproj$").find(segment)?.let { return it.groupValues[1] }
    }
    val name = segments.last().substringBeforeLast('.')
    return if (name.matches(Regex("[a-z]{2}(-[A-Z]{2})?"))) name else ""
}

private fun detectFormat(name: String): FileFormat? =
    when (name.substringAfterLast('.', "").lowercase()) {
        "xml" -> FileFormat.ANDROID_XML
        "strings" -> FileFormat.IOS_STRINGS
        "stringsdict" -> FileFormat.IOS_STRINGSDICT
        "xcstrings" -> FileFormat.XCSTRINGS
        "json" -> FileFormat.JSON_NESTED
        "yaml", "yml" -> FileFormat.YAML
        "properties" -> FileFormat.PROPERTIES
        "po" -> FileFormat.PO
        "csv" -> FileFormat.CSV
        "xliff", "xlf" -> FileFormat.XLIFF
        else -> null
    }

/**
 * Reads the chosen file, and says which file the text belongs to.
 *
 * Two defects lived in the three lines this replaces. There was no `onerror`, so a file the browser
 * could not read left `content` null for good — the "Plan the import" card never appeared and
 * nothing on screen said why. And the callback carried no identity, so choosing file A and then
 * file B before A's `onload` fired left A's *text* sitting under B's path, format and locale: the
 * `ImportRequest` then described one file and carried another, and the plan shown was A's diff
 * under B's name.
 */
private fun readText(file: File, onLoaded: (File, String) -> Unit, onFailed: (File) -> Unit) {
    val reader = FileReader()
    reader.onload = { onLoaded(file, reader.result as? String ?: "") }
    reader.onerror = { onFailed(file) }
    reader.readAsText(file)
}

/**
 * The file picker, styled as the secondary control it stands in for.
 *
 * The `<input type=file>` inside is visually hidden, so this label is the only thing on screen and
 * the only thing a reader can click. Left to itself the input paints a user-agent button in a
 * user-agent font with square corners and the words "No file chosen" beside it — untranslated, and
 * in Night a light-grey slab on an ink card, the one control in the product that ignores the colour
 * mode. It is also the first control in the page's primary task, so that was the loudest broken
 * thing on the screen sitting where the reader is asked to start.
 *
 * `:focus-within` rather than `:focus`: focus lands on the hidden input, so a ring on the label
 * itself would never appear. Height, radius, border and weight match
 * `ProjectIconUploadLabelStyle` — the same trick, played for the same reason, on the other file
 * picker in the product.
 */
val DataFilePickerLabelStyle = CssStyle {
    base {
        Modifier
            .height(FILE_PICKER_HEIGHT_PX.px)
            .padding(leftRight = Space.LG)
            .borderRadius(Radius.Control)
            // `borderStrong`, the boundary a secondary button draws. `border` is the divider token
            // and `docs/DESIGN.md` marks it illegal as a control boundary.
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .backgroundColor(colorMode.toSitePalette().surface)
            .color(colorMode.toSitePalette().textPrimary)
            .fontSize(TypeScale.BodySmall)
            .fontWeight(600)
            .cursor(Cursor.Pointer)
            .transitionColors()
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("align-self", "flex-start")
                property("gap", "8px")
                property("box-sizing", "border-box")
                property("white-space", "nowrap")
            }
    }
    hover { Modifier.backgroundColor(colorMode.toSitePalette().surfaceSunken) }
    cssRule(":focus-within") {
        Modifier.styleModifier {
            property("outline", "2px solid ${colorMode.toSitePalette().accent}")
            property("outline-offset", "2px")
        }
    }
}

/** `LButtonSize.MD`, so the picker sits in a card with real buttons without looking like a near-miss. */
private const val FILE_PICKER_HEIGHT_PX = 40
